/**
 * QuarterlyForecastCard — shows quarterly + monthly KPI targets vs actuals
 * Supports all dashboard modes: lead_gen_basic, lead_gen_advanced, ecommerce, saas.
 */
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Phone, DollarSign, TrendingUp, Calendar, ShoppingCart } from "lucide-react";
import type { QuarterlyTargets, QuarterlyTarget, MonthlyTargets } from "@shared/schema";

// ── Dashboard mode type ──
type DashboardMode = "lead_gen_basic" | "lead_gen_advanced" | "ecommerce" | "saas" | string;

interface QuarterlyForecastCardProps {
  quarterlyTargets: QuarterlyTargets | null;
  monthlyTargets?: MonthlyTargets | null;
  actualLeads: number;
  actualCalls: number;
  actualSales: number;
  actualPurchaseValue?: number;
  actualPurchases?: number;
  actualRoas?: number;
  actualTrials?: number;
  actualPaid?: number;
  actualMrr?: number;
  dashboardMode?: DashboardMode;
}

// ── Date helpers ──

function getCurrentQuarterKey(): string {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `${now.getFullYear()}-Q${q}`;
}

function getCurrentMonthKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function getQuarterDates(qKey: string): { start: Date; end: Date } {
  const [yearStr, qStr] = qKey.split("-");
  const year = parseInt(yearStr);
  const q = parseInt(qStr.replace("Q", ""));
  const startMonth = (q - 1) * 3;
  return {
    start: new Date(year, startMonth, 1),
    end: new Date(year, startMonth + 3, 0, 23, 59, 59),
  };
}

function getMonthDates(mKey: string): { start: Date; end: Date } {
  const [yearStr, monthStr] = mKey.split("-");
  const year = parseInt(yearStr);
  const month = parseInt(monthStr) - 1;
  return {
    start: new Date(year, month, 1),
    end: new Date(year, month + 1, 0, 23, 59, 59),
  };
}

function getPeriodProgress(dates: { start: Date; end: Date }): number {
  const { start, end } = dates;
  const now = new Date();
  const total = end.getTime() - start.getTime();
  const elapsed = Math.max(0, Math.min(now.getTime() - start.getTime(), total));
  return total > 0 ? elapsed / total : 0;
}

// ── Forecast logic ──

type ForecastStatus = "ahead" | "on-track" | "behind";

function computeForecast(
  actual: number,
  target: number | null | undefined,
  periodFraction: number
): { forecast: number; progress: number; status: ForecastStatus } {
  if (!target || target <= 0) return { forecast: 0, progress: 0, status: "on-track" };
  const forecast = periodFraction > 0 ? Math.round(actual / periodFraction) : 0;
  const progress = actual / target;
  const ratio = periodFraction > 0 ? progress / periodFraction : 0;
  let status: ForecastStatus = "on-track";
  if (ratio >= 1.1) status = "ahead";
  if (ratio < 0.85) status = "behind";
  return { forecast, progress, status };
}

const STATUS_LABEL_KEYS: Record<ForecastStatus, { labelKey: string; color: string; bg: string }> = {
  ahead: {
    labelKey: "quarterlyForecast.status.ahead",
    color: "text-emerald-400",
    bg: "bg-emerald-500",
  },
  "on-track": {
    labelKey: "quarterlyForecast.status.onTrack",
    color: "text-blue-400",
    bg: "bg-blue-500",
  },
  behind: {
    labelKey: "quarterlyForecast.status.behind",
    color: "text-amber-400",
    bg: "bg-amber-500",
  },
};

// ── Per-mode metric config ──

interface RawMetric {
  labelKey: string;
  icon: typeof Users;
  target: number | null | undefined;
  actual: number;
  formatValue?: (v: number) => string;
}

function getMetrics(
  target: QuarterlyTarget,
  props: QuarterlyForecastCardProps,
  mode: DashboardMode,
  locale: string,
  currencySuffix: string
): RawMetric[] {
  const fmt = (v: number) => `${v.toLocaleString(locale)}${currencySuffix}`;
  const fmtRoas = (v: number) => v.toFixed(2) + "x";

  if (mode === "ecommerce") {
    return [
      {
        labelKey: "quarterlyForecast.metric.purchaseValue",
        icon: DollarSign,
        target: target.targetPurchaseValue,
        actual: props.actualPurchaseValue ?? 0,
        formatValue: fmt,
      },
      {
        labelKey: "quarterlyForecast.metric.purchases",
        icon: ShoppingCart,
        target: target.targetPurchases,
        actual: props.actualPurchases ?? 0,
      },
      {
        labelKey: "quarterlyForecast.metric.roas",
        icon: TrendingUp,
        target: target.targetRoas,
        actual: props.actualRoas ?? 0,
        formatValue: fmtRoas,
      },
      {
        labelKey: "quarterlyForecast.metric.costPerPurchase",
        icon: DollarSign,
        target: target.targetCostPerPurchase,
        actual: 0,
        formatValue: fmt,
      },
    ];
  }

  if (mode === "saas") {
    return [
      {
        labelKey: "quarterlyForecast.metric.trials",
        icon: Users,
        target: target.targetTrials,
        actual: props.actualTrials ?? 0,
      },
      {
        labelKey: "quarterlyForecast.metric.paid",
        icon: DollarSign,
        target: target.targetPaid,
        actual: props.actualPaid ?? 0,
      },
      {
        labelKey: "quarterlyForecast.metric.mrr",
        icon: TrendingUp,
        target: target.targetMrr,
        actual: props.actualMrr ?? 0,
        formatValue: fmt,
      },
    ];
  }

  if (mode === "lead_gen_advanced") {
    return [
      {
        labelKey: "quarterlyForecast.metric.leads",
        icon: Users,
        target: target.targetLeads,
        actual: props.actualLeads,
      },
      {
        labelKey: "quarterlyForecast.metric.calls",
        icon: Phone,
        target: target.targetCalls,
        actual: props.actualCalls,
      },
      {
        labelKey: "quarterlyForecast.metric.sales",
        icon: DollarSign,
        target: target.targetSales,
        actual: props.actualSales,
      },
      {
        labelKey: "quarterlyForecast.metric.roas",
        icon: TrendingUp,
        target: target.targetRoas,
        actual: props.actualRoas ?? 0,
        formatValue: fmtRoas,
      },
    ];
  }

  return [
    {
      labelKey: "quarterlyForecast.metric.leads",
      icon: Users,
      target: target.targetLeads,
      actual: props.actualLeads,
    },
    {
      labelKey: "quarterlyForecast.metric.sales",
      icon: DollarSign,
      target: target.targetSales,
      actual: props.actualSales,
    },
  ];
}

// ── Period block ──

function PeriodBlock({
  label,
  isCurrent,
  fraction,
  fractionLabel,
  target,
  props,
  mode,
}: {
  label: string;
  isCurrent: boolean;
  fraction: number;
  fractionLabel: string;
  target: QuarterlyTarget;
  props: QuarterlyForecastCardProps;
  mode: DashboardMode;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "pl" ? "pl-PL" : "en-US";
  const currencySuffix = i18n.language === "pl" ? " zł" : " $";
  const rawMetrics = getMetrics(target, props, mode, locale, currencySuffix);
  const metrics = rawMetrics
    .filter((m) => m.target && m.target > 0)
    .map((m) => {
      const { forecast, progress, status } = computeForecast(m.actual, m.target, fraction);
      return { ...m, forecast, progress, status };
    });

  if (metrics.length === 0) return null;

  const fmt = (m: (typeof metrics)[0], v: number) => (m.formatValue ? m.formatValue(v) : String(v));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-white">{label}</span>
          {isCurrent && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-600/30 text-blue-300">
              {t("quarterlyForecast.current")}
            </span>
          )}
        </div>
        <span className="text-[10px] text-white/40">{fractionLabel}</span>
      </div>

      {/* Period progress bar */}
      <div className="w-full h-1 bg-white/[0.08] rounded-full overflow-hidden">
        <div
          className="h-full bg-white/20 rounded-full transition-all"
          style={{ width: `${Math.round(fraction * 100)}%` }}
        />
      </div>

      {metrics.map((m) => {
        const Icon = m.icon;
        const statusCfg = STATUS_LABEL_KEYS[m.status];
        const progressPct = Math.min(m.progress * 100, 100);
        return (
          <div key={m.labelKey} className="space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Icon className="w-3.5 h-3.5 text-white/50" />
                <span className="text-xs text-white/70">{t(m.labelKey)}</span>
              </div>
              <span className={`text-[10px] ${statusCfg.color}`}>{t(statusCfg.labelKey)}</span>
            </div>
            <div className="w-full h-2 bg-white/[0.06] rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${statusCfg.bg}`}
                style={{ width: `${progressPct}%`, opacity: 0.8 }}
              />
            </div>
            <div className="flex items-center justify-between text-[10px]">
              <span className="text-white/50">
                {fmt(m, m.actual)} / {fmt(m, m.target!)}
                <span className="text-white/30 ml-1">({Math.round(m.progress * 100)}%)</span>
              </span>
              <div className="flex items-center gap-1 text-white/40">
                <TrendingUp className="w-2.5 h-2.5" />
                <span>
                  {t("quarterlyForecast.forecast")}:{" "}
                  <strong className="text-white/70">{fmt(m, m.forecast)}</strong>
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main export ──

export default function QuarterlyForecastCard(props: QuarterlyForecastCardProps) {
  const { t } = useTranslation();
  const { quarterlyTargets, monthlyTargets, dashboardMode = "lead_gen_basic" } = props;
  const [tab, setTab] = useState<"quarterly" | "monthly">("quarterly");

  const qKey = getCurrentQuarterKey();
  const mKey = getCurrentMonthKey();

  const currentQTarget = quarterlyTargets?.[qKey];
  const currentMTarget = monthlyTargets?.[mKey];

  const hasQuarterlyTargets = useMemo(() => {
    if (!currentQTarget) return false;
    return Object.values(currentQTarget).some(
      (v) => v !== null && v !== undefined && Number(v) > 0
    );
  }, [currentQTarget]);

  const hasMonthlyTargets = useMemo(() => {
    if (!currentMTarget) return false;
    return Object.values(currentMTarget).some(
      (v) => v !== null && v !== undefined && Number(v) > 0
    );
  }, [currentMTarget]);

  if (!hasQuarterlyTargets && !hasMonthlyTargets) return null;

  // Adjust tab if only one type is available
  const activeTab: "quarterly" | "monthly" =
    tab === "quarterly" && !hasQuarterlyTargets
      ? "monthly"
      : tab === "monthly" && !hasMonthlyTargets
        ? "quarterly"
        : tab;

  const qFraction = hasQuarterlyTargets ? getPeriodProgress(getQuarterDates(qKey)) : 0;
  const mFraction = hasMonthlyTargets ? getPeriodProgress(getMonthDates(mKey)) : 0;

  const MONTH_NAME_KEYS = [
    "quarterlyForecast.month.january",
    "quarterlyForecast.month.february",
    "quarterlyForecast.month.march",
    "quarterlyForecast.month.april",
    "quarterlyForecast.month.may",
    "quarterlyForecast.month.june",
    "quarterlyForecast.month.july",
    "quarterlyForecast.month.august",
    "quarterlyForecast.month.september",
    "quarterlyForecast.month.october",
    "quarterlyForecast.month.november",
    "quarterlyForecast.month.december",
  ];
  const formatQLabel = (key: string) => {
    const [y, q] = key.split("-");
    return `${q} ${y}`;
  };
  const formatMLabel = (key: string) => {
    const [y, m] = key.split("-");
    return `${t(MONTH_NAME_KEYS[parseInt(m) - 1])} ${y}`;
  };

  return (
    <Card className="bg-[#0d0f14] border-white/[0.08]">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-400" />
            <h3 className="text-sm font-semibold text-white">{t("quarterlyForecast.title")}</h3>
          </div>
          {/* Tab switcher */}
          {hasQuarterlyTargets && hasMonthlyTargets && (
            <div className="flex gap-1 bg-white/[0.04] rounded-md p-0.5">
              {(["quarterly", "monthly"] as const).map((tabKey) => (
                <button
                  key={tabKey}
                  onClick={() => setTab(tabKey)}
                  className={`text-[10px] px-2.5 py-0.5 rounded transition-colors ${
                    activeTab === tabKey
                      ? "bg-white/[0.12] text-white"
                      : "text-white/40 hover:text-white/70"
                  }`}
                >
                  {tabKey === "quarterly"
                    ? t("quarterlyForecast.tab.quarterly")
                    : t("quarterlyForecast.tab.monthly")}
                </button>
              ))}
            </div>
          )}
          {!hasMonthlyTargets && (
            <span className="text-[10px] text-white/40">
              {Math.round(qFraction * 100)}% {t("quarterlyForecast.ofQuarter")}
            </span>
          )}
          {!hasQuarterlyTargets && (
            <span className="text-[10px] text-white/40">
              {Math.round(mFraction * 100)}% {t("quarterlyForecast.ofMonth")}
            </span>
          )}
        </div>

        {activeTab === "quarterly" && currentQTarget && (
          <PeriodBlock
            label={formatQLabel(qKey)}
            isCurrent={true}
            fraction={qFraction}
            fractionLabel={`${Math.round(qFraction * 100)}% ${t("quarterlyForecast.ofQuarter")}`}
            target={currentQTarget}
            props={props}
            mode={dashboardMode}
          />
        )}

        {activeTab === "monthly" && currentMTarget && (
          <PeriodBlock
            label={formatMLabel(mKey)}
            isCurrent={true}
            fraction={mFraction}
            fractionLabel={`${Math.round(mFraction * 100)}% ${t("quarterlyForecast.ofMonth")}`}
            target={currentMTarget}
            props={props}
            mode={dashboardMode}
          />
        )}
      </CardContent>
    </Card>
  );
}
